import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { mcaI20Logo } from "../../../Assets";
import KenLoader from "../../../GlobalComponents/KenLoader";
import { useToast } from "../../../Helpers/Contexts/toastContext";
import { verifyLogin } from "../../../Utils/apiService";
import routes from "../../../Utils/routes";

type LoginFormInputs = {
  username: string;
  password: string;
};

const Login: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>();

  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  const onSubmit = async (data: LoginFormInputs) => {
    const { username, password } = data;
    const payload = { email: username, password };
    try {
      setLoading(true);
      const res = await verifyLogin(payload);
      if (res?.data?.success) {
        const userData = res.data.data?.data;
        const recordType = userData?.recordType;

        if (recordType === "Team Owner") {
          // Team Owner flow
          localStorage.setItem("isTeamAuthenticated", "true");
          localStorage.setItem("teamId", userData?.AccountId);
          navigate(routes.teamDashboard);
          return;
        } else if (recordType === "Admin") {
          // Admin flow
          localStorage.setItem("userDetails", JSON.stringify(userData));
          localStorage.setItem("isAuthenticated", "true");
          navigate(routes.dashboard);
          return;
        } else {
          // Unknown role fallback
          showToast("error", "Unauthorized access: unknown user role.");
        }
      }
    } catch (err: any) {
      console.error("Login error:", err);
      showToast(
        "error",
        err?.response?.data?.message || "Login failed. Try again."
      );
    } finally {
      setLoading(false);
    }
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <>
      {loading ? (
        <KenLoader />
      ) : (
        <Box
          sx={{
            minHeight: "100dvh",
            width: "100%",
            px: { xs: 1.25, sm: 3, md: 4 },
            py: { xs: 3, sm: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#09090B",
            position: "relative",
            overflowX: "hidden",
            overflowY: "hidden",
          }}
        >
          <Box
            sx={{
              background: "#E97D45",
              filter: "blur(50vw)",
              width: "50vw",
              height: "50vw",
              borderRadius: "50vw",
              opacity: "0.3",
              position: "absolute",
              top: "-25vw",
              right: "-25vw",
            }}
          />
          <Box
            sx={{
              background: "#E97D45",
              filter: "blur(50vw)",
              width: "50vw",
              height: "50vw",
              borderRadius: "50vw",
              opacity: "0.3",
              position: "absolute",
              bottom: "-25vw",
              left: "-10vw",
            }}
          />
          <Box
            sx={{
              width: { xs: "128px", sm: "144px", md: "160px" },
              mb: { xs: 3, sm: 4, md: 7 },
              zIndex: 1,
            }}
          >
            <img
              src={mcaI20Logo}
              style={{ width: "100%", height: "auto" }}
              alt="MCA Logo"
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "360px", sm: "520px", md: "600px" },
              mx: "auto",
              zIndex: 1,
              boxSizing: "border-box",
            }}
          >
            <Paper
              sx={{
                p: { xs: "20px 16px", sm: "32px 24px", md: "64px 48px" },
                width: "100%",
                bgcolor: "#0D0D0D",
                border: "1px solid rgba(255, 255, 255, 0.20)",
                borderRadius: "12px",
                boxSizing: "border-box",
              }}
            >
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box display="flex" flexDirection="column" gap={{ xs: 1.5, sm: 2 }}>
                <Controller
                  name="username"
                  control={control}
                  rules={{
                    required: "*email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Enter email ID"
                      fullWidth
                      error={!!errors.username}
                      helperText={errors.username?.message}
                      FormHelperTextProps={{ sx: { fontSize: "11px" } }}
                      sx={{
                        height: { xs: "54px", md: "60px" },
                        "& .MuiOutlinedInput-root": {
                          height: { xs: "54px", md: "60px" },
                        },
                        "& label": {
                          color: "#B5B3B3",
                          fontSize: { xs: "12px", md: "14px" },
                          fontWeight: "400",
                        },
                        "& label.Mui-focused": {
                          color: "#B5B3B3",
                        },
                      }}
                      InputProps={{
                        sx: {
                          "&.Mui-focused": { color: "#fff" },
                          height: { xs: "54px", md: "60px" },
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  rules={{ required: "Password is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Enter password"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      FormHelperTextProps={{ sx: { fontSize: "11px" } }}
                      sx={{
                        height: { xs: "54px", md: "60px" },
                        "& .MuiOutlinedInput-root": {
                          height: { xs: "54px", md: "60px" },
                        },
                        "& label": {
                          color: "#B5B3B3",
                          fontSize: { xs: "12px", md: "14px" },
                          fontWeight: "400",
                        },
                        "& label.Mui-focused": {
                          color: "#B5B3B3",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ color: "#fff" }} />
                              ) : (
                                <Visibility sx={{ color: "#fff" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          height: { xs: "54px", md: "60px" },
                        },
                      }}
                    />
                  )}
                />
                {/* <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: "16px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "500", color: "#fff" }}
                  >
                    Forgot password?
                  </Typography>
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "500", color: "#fff" }}
                  >
                    Contact admin
                  </Typography>
                </Box> */}

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    background: "#ffff",
                    ":hover": { background: "#ffff" },
                    color: "#0D0D0D",
                    height: { xs: "46px", md: "48px" },
                    borderRadius: "8px",
                    fontSize: { xs: "16px", md: "18px" },
                    fontWeight: "500",
                  }}
                >
                  Login
                </Button>
              </Box>
            </form>
            </Paper>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Login;
